import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle, Bot, Check, ChevronRight, Image as ImageIcon,
  Loader2, RefreshCw, Sparkles, Trash2, X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  applyRescue, askAi, deleteFailure, fetchFailureScreenshot, getFailure,
  ignoreRescue, listFailures, listRescueModels,
  type DSLStep, type RescueAttempt, type RescueModel, type ScriptFailure,
} from "../api/failures";
import { relativeTime, formatDateTime } from "../utils/format";

// ---------------------------------------------------------------------------
// FailuresPage — liste de toutes les failures de step capturées par les
// tweaks (Phase 0). Bouton "Ask AI" sur chaque détail pour déclencher
// l'analyse Claude / GPT et obtenir un patch suggéré (Phase 1).
//
// Layout : sidebar 320px avec la liste des failures (sélectionnable) +
// panneau de droite avec le détail complet de la sélection (screenshot +
// JSON context + bouton Ask AI + historique des rescues).
// ---------------------------------------------------------------------------

const ERROR_KIND_LABELS: Record<string, string> = {
  timeout:        "Timeout",
  no_keyboard:    "No keyboard",
  http_status:    "HTTP status",
  http_transport: "Network",
  fail_explicit:  "Explicit fail",
  ocr_not_found:  "OCR not found",
  invalid_step:   "Invalid step",
  unknown:        "Unknown",
};

const ERROR_KIND_COLORS: Record<string, string> = {
  timeout:        "bg-amber-500/15 text-amber-300 border-amber-500/30",
  no_keyboard:    "bg-violet-500/15 text-violet-300 border-violet-500/30",
  http_status:    "bg-rose-500/15 text-rose-300 border-rose-500/30",
  http_transport: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  fail_explicit:  "bg-red-500/15 text-red-300 border-red-500/30",
  ocr_not_found:  "bg-sky-500/15 text-sky-300 border-sky-500/30",
  invalid_step:   "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30",
  unknown:        "bg-ink-700/40 text-ink-400 border-ink-600",
};

export function FailuresPage(): JSX.Element {
  const { t } = useTranslation();
  const [list, setList] = useState<ScriptFailure[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setError(null);
      const { failures } = await listFailures({ limit: 100 });
      setList(failures);
      if (failures.length > 0 && !selectedId) {
        setSelectedId(failures[0].id);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : t("common.error"));
    } finally {
      setLoading(false);
    }
  }, [selectedId, t]);

  useEffect(() => {
    void refresh();
    const iv = setInterval(() => void refresh(), 30_000);
    return () => clearInterval(iv);
  }, [refresh]);

  function handleDeleted(id: string) {
    setList((cur) => cur.filter((f) => f.id !== id));
    if (selectedId === id) {
      setSelectedId(null);
    }
  }

  return (
    <div className="flex h-screen">
      <aside className="w-80 shrink-0 border-r border-ink-700 flex flex-col">
        <header className="p-4 border-b border-ink-700">
          <div className="flex items-center justify-between">
            <h1 className="text-lg font-bold">{t("failures.title")}</h1>
            <button
              onClick={() => void refresh()}
              className="p-1.5 rounded text-ink-500 hover:text-white hover:bg-ink-700/50"
              title={t("common.refresh")}
            >
              <RefreshCw className="size-4" />
            </button>
          </div>
          <p className="mt-1 text-xs text-ink-500">
            {list.length} {t("failures.title").toLowerCase()}
          </p>
        </header>
        <div className="flex-1 overflow-y-auto">
          {loading && (
            <div className="p-4 text-sm text-ink-500 flex items-center gap-2">
              <Loader2 className="size-4 animate-spin" /> {t("common.loading")}
            </div>
          )}
          {error && (
            <div className="m-3 p-3 rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 text-sm">
              {error}
            </div>
          )}
          {!loading && list.length === 0 && (
            <div className="p-6 text-sm text-ink-500 text-center">
              {t("failures.empty")}
            </div>
          )}
          {list.map((f) => (
            <FailureListItem
              key={f.id}
              failure={f}
              selected={f.id === selectedId}
              onClick={() => setSelectedId(f.id)}
            />
          ))}
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto">
        {selectedId ? (
          <FailureDetail
            failureId={selectedId}
            onDeleted={handleDeleted}
          />
        ) : (
          <div className="h-full flex items-center justify-center text-ink-500 text-sm">
            Sélectionne une failure dans la liste à gauche.
          </div>
        )}
      </main>
    </div>
  );
}

function FailureListItem({
  failure,
  selected,
  onClick,
}: {
  failure: ScriptFailure;
  selected: boolean;
  onClick: () => void;
}): JSX.Element {
  const kindLabel = failure.error_kind
    ? ERROR_KIND_LABELS[failure.error_kind] ?? failure.error_kind
    : "Unknown";
  const kindColor = failure.error_kind
    ? ERROR_KIND_COLORS[failure.error_kind] ?? ERROR_KIND_COLORS.unknown
    : ERROR_KIND_COLORS.unknown;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left px-4 py-3 border-b border-ink-700 hover:bg-ink-700/30 transition-colors ${
        selected ? "bg-velvet-600/10 border-l-2 border-l-velvet-500" : ""
      }`}
    >
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="font-mono text-xs text-ink-300 truncate">
          {failure.step_path}
        </div>
        <span
          className={`shrink-0 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${kindColor}`}
        >
          {kindLabel}
        </span>
      </div>
      <div className="text-sm text-white truncate mb-1">
        {failure.step_type}
      </div>
      <div className="text-xs text-ink-500 truncate" title={failure.error_message}>
        {failure.error_message}
      </div>
      <div className="mt-1 flex items-center justify-between text-[11px] text-ink-600">
        <span>{failure.script_name ?? "?"} · {failure.phone_name ?? "?"}</span>
        <span title={formatDateTime(failure.created_at)}>
          {relativeTime(failure.created_at)}
        </span>
      </div>
      {failure.has_screenshot && (
        <div className="mt-1 inline-flex items-center gap-1 text-[10px] text-ink-500">
          <ImageIcon className="size-3" /> screenshot
        </div>
      )}
    </button>
  );
}

function FailureDetail({
  failureId,
  onDeleted,
}: {
  failureId: string;
  onDeleted: (id: string) => void;
}): JSX.Element {
  const [failure, setFailure] = useState<ScriptFailure | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [imgUrl, setImgUrl] = useState<string | null>(null);
  const [askingAi, setAskingAi] = useState(false);
  const [models, setModels] = useState<RescueModel[]>([]);
  const [selectedModel, setSelectedModel] = useState<string>("");
  const [aiError, setAiError] = useState<string | null>(null);

  // Charge la failure + screenshot quand l'id change.
  useEffect(() => {
    let cancelled = false;
    let revokedUrl: string | null = null;
    setLoading(true);
    setError(null);
    setFailure(null);
    setImgUrl(null);
    setAiError(null);

    (async () => {
      try {
        const f = await getFailure(failureId);
        if (cancelled) return;
        setFailure(f);
        if (f.has_screenshot) {
          try {
            const url = await fetchFailureScreenshot(failureId);
            if (cancelled) {
              URL.revokeObjectURL(url);
            } else {
              setImgUrl(url);
              revokedUrl = url;
            }
          } catch (e) {
            console.warn("screenshot fetch failed:", e);
          }
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Erreur de chargement");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (revokedUrl) URL.revokeObjectURL(revokedUrl);
    };
  }, [failureId]);

  // Charge la liste des modèles une fois.
  useEffect(() => {
    listRescueModels()
      .then((d) => {
        setModels(d.models);
        if (!selectedModel) setSelectedModel(d.default);
      })
      .catch((e) => console.warn("models fetch failed:", e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleAskAi() {
    if (!failure) return;
    setAskingAi(true);
    setAiError(null);
    try {
      const rescue = await askAi(failure.id, selectedModel || undefined);
      // Recharge le failure pour avoir la liste à jour des rescue_attempts.
      const updated = await getFailure(failure.id);
      setFailure(updated);
      if (rescue.error_message) {
        setAiError(rescue.error_message);
      }
    } catch (e) {
      setAiError(e instanceof Error ? e.message : "Ask AI failed");
    } finally {
      setAskingAi(false);
    }
  }

  async function handleDelete() {
    if (!failure) return;
    if (!confirm("Supprimer cette failure et son screenshot ?")) return;
    try {
      await deleteFailure(failure.id);
      onDeleted(failure.id);
    } catch (e) {
      alert(e instanceof Error ? e.message : "Suppression échouée");
    }
  }

  const handleRescueChanged = useCallback(async () => {
    if (!failure) return;
    try {
      const updated = await getFailure(failure.id);
      setFailure(updated);
    } catch (e) {
      console.warn("refresh after rescue change failed:", e);
    }
  }, [failure]);

  if (loading) {
    return (
      <div className="p-8 flex items-center gap-2 text-ink-500">
        <Loader2 className="size-5 animate-spin" /> Chargement…
      </div>
    );
  }
  if (error || !failure) {
    return (
      <div className="p-8 text-red-300">
        {error ?? "Failure introuvable"}
      </div>
    );
  }

  return (
    <div className="p-8 max-w-5xl space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <AlertTriangle className="size-5 text-amber-400" />
            {failure.step_type}
          </h2>
          <p className="mt-1 text-sm text-ink-500 font-mono">{failure.step_path}</p>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-ink-500">
            <span>{failure.script_name ?? "?"}</span>
            <span>·</span>
            <span>{failure.phone_name ?? "?"}</span>
            <span>·</span>
            <span title={formatDateTime(failure.created_at)}>
              {relativeTime(failure.created_at)}
            </span>
          </div>
        </div>
        <button
          onClick={() => void handleDelete()}
          className="px-3 py-1.5 rounded text-red-400 hover:text-red-300 hover:bg-red-500/10 text-sm inline-flex items-center gap-1"
        >
          <Trash2 className="size-4" /> Supprimer
        </button>
      </header>

      {/* Error message */}
      <section className="p-4 rounded-lg border border-red-500/30 bg-red-500/10">
        <div className="text-xs text-red-300 uppercase tracking-wider font-semibold mb-1">
          Erreur
        </div>
        <div className="text-sm text-red-100 font-mono whitespace-pre-wrap break-words">
          {failure.error_message}
        </div>
      </section>

      {/* Layout deux colonnes : screen + ctx | Ask AI */}
      <div className="grid grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <h3 className="text-sm font-semibold text-ink-300 mb-2">Screenshot</h3>
            {imgUrl ? (
              <img
                src={imgUrl}
                alt="screenshot"
                className="w-full max-h-[600px] object-contain rounded-lg border border-ink-700 bg-black"
              />
            ) : (
              <div className="aspect-[9/19] flex items-center justify-center rounded-lg border border-dashed border-ink-700 text-ink-500 text-xs">
                {failure.has_screenshot ? "Chargement…" : "Pas de screenshot"}
              </div>
            )}
          </div>

          <JsonBlock title="Step qui a échoué" value={failure.step_definition} />
          {failure.preceding_steps?.length > 0 && (
            <JsonBlock title="Steps précédents" value={failure.preceding_steps} />
          )}
          {failure.ocr_items && failure.ocr_items.length > 0 && (
            <JsonBlock
              title={`OCR items (${failure.ocr_items.length})`}
              value={failure.ocr_items}
              collapsedByDefault
            />
          )}
          {failure.runtime_vars && Object.keys(failure.runtime_vars).length > 0 && (
            <JsonBlock
              title="Runtime vars"
              value={failure.runtime_vars}
              collapsedByDefault
            />
          )}
        </div>

        {/* Colonne IA */}
        <div className="space-y-4">
          <section className="p-4 rounded-lg border border-velvet-500/30 bg-velvet-500/5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-velvet-200 flex items-center gap-2">
                <Sparkles className="size-4" /> Ask AI for a fix
              </h3>
            </div>
            <div className="space-y-2">
              <label className="block text-xs text-ink-500">Modèle</label>
              <select
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)}
                className="w-full px-2 py-1.5 rounded bg-ink-900 border border-ink-700 focus:border-velvet-500 focus:outline-none text-white text-sm"
              >
                {models.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.label}
                  </option>
                ))}
              </select>
              <button
                onClick={() => void handleAskAi()}
                disabled={askingAi}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-velvet-600 hover:bg-velvet-500 text-white text-sm font-medium disabled:opacity-50"
              >
                {askingAi ? (
                  <>
                    <Loader2 className="size-4 animate-spin" /> Analyse en cours…
                  </>
                ) : (
                  <>
                    <Bot className="size-4" /> Demander un patch
                  </>
                )}
              </button>
              {aiError && (
                <div className="p-2 rounded border border-red-500/30 bg-red-500/10 text-red-300 text-xs">
                  {aiError}
                </div>
              )}
            </div>
          </section>

          {failure.rescue_attempts && failure.rescue_attempts.length > 0 && (
            <section className="space-y-3">
              <h3 className="text-sm font-semibold text-ink-300">
                Rescue attempts ({failure.rescue_attempts.length})
              </h3>
              {failure.rescue_attempts.map((ra) => (
                <RescueCard
                  key={ra.id}
                  rescue={ra}
                  onChanged={handleRescueChanged}
                />
              ))}
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

function RescueCard({
  rescue,
  onChanged,
}: {
  rescue: RescueAttempt;
  onChanged: () => void | Promise<void>;
}): JSX.Element {
  const [applying, setApplying] = useState(false);
  const [showRaw, setShowRaw] = useState(false);

  const statusColor = useMemo(() => {
    switch (rescue.status) {
      case "applied": return "bg-emerald-500/15 text-emerald-300 border-emerald-500/30";
      case "ignored": return "bg-ink-700/40 text-ink-400 border-ink-600";
      case "errored": return "bg-red-500/15 text-red-300 border-red-500/30";
      default:        return "bg-amber-500/15 text-amber-300 border-amber-500/30";
    }
  }, [rescue.status]);

  const confidenceLabel = rescue.suggestion?.confidence ?? null;

  async function handleApply() {
    setApplying(true);
    try {
      const result = await applyRescue(rescue.id);
      // Affiche les steps à copier-coller dans une dialog simple.
      const yamlish = JSON.stringify(result.recovery_steps, null, 2);
      alert(
        `✓ Patch appliqué (audit only — pas de modification automatique du script).\n\n` +
        `Cible : ${result.target_step_path}\n\n` +
        `Steps à intégrer dans le script :\n${yamlish}`
      );
      await onChanged();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Apply failed");
    } finally {
      setApplying(false);
    }
  }

  async function handleIgnore() {
    try {
      await ignoreRescue(rescue.id);
      await onChanged();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Ignore failed");
    }
  }

  const validationOk = rescue.validation?.ok ?? false;

  return (
    <article className="p-3 rounded-lg border border-ink-700 bg-ink-800/40 space-y-2">
      <header className="flex items-center justify-between gap-2 text-xs">
        <div className="flex items-center gap-2">
          <span className={`uppercase tracking-wider px-1.5 py-0.5 rounded border ${statusColor}`}>
            {rescue.status}
          </span>
          {confidenceLabel && (
            <span className="text-ink-500">conf: {confidenceLabel}</span>
          )}
        </div>
        <div className="text-ink-500 font-mono">
          {rescue.model} · {rescue.input_tokens ?? 0}+{rescue.output_tokens ?? 0} tok · {rescue.cost_cents ?? 0}¢
        </div>
      </header>

      {rescue.error_message && (
        <div className="p-2 rounded border border-red-500/30 bg-red-500/10 text-red-300 text-xs">
          {rescue.error_message}
        </div>
      )}

      {rescue.suggestion?.diagnosis && (
        <div>
          <div className="text-xs text-ink-500 uppercase tracking-wider mb-1">Diagnosis</div>
          <p className="text-sm text-ink-200">{rescue.suggestion.diagnosis}</p>
        </div>
      )}

      {rescue.suggestion?.recovery_steps && (
        <JsonBlock
          title={`Recovery steps (${rescue.suggestion.recovery_steps.length})`}
          value={rescue.suggestion.recovery_steps}
        />
      )}

      {!validationOk && rescue.validation && rescue.validation.errors.length > 0 && (
        <div className="p-2 rounded border border-amber-500/30 bg-amber-500/10 text-amber-200 text-xs">
          <div className="font-semibold mb-1">Validation errors :</div>
          <ul className="list-disc list-inside space-y-0.5">
            {rescue.validation.errors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>
      )}

      <footer className="flex items-center justify-between pt-1">
        <button
          onClick={() => setShowRaw((v) => !v)}
          className="text-[11px] text-ink-500 hover:text-ink-300"
        >
          {showRaw ? "Hide raw" : "Show raw response"}
        </button>
        {rescue.status === "pending" && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => void handleIgnore()}
              className="px-2 py-1 rounded text-xs text-ink-400 hover:text-white hover:bg-ink-700/50"
            >
              <X className="size-3 inline mr-1" /> Ignorer
            </button>
            <button
              onClick={() => void handleApply()}
              disabled={applying || !validationOk}
              className="px-3 py-1 rounded bg-velvet-600 hover:bg-velvet-500 text-white text-xs font-medium disabled:opacity-50 inline-flex items-center gap-1"
            >
              {applying ? <Loader2 className="size-3 animate-spin" /> : <Check className="size-3" />}
              Apply
            </button>
          </div>
        )}
      </footer>

      {showRaw && rescue.response_raw && (
        <pre className="text-[11px] text-ink-500 bg-ink-900 rounded p-2 overflow-x-auto whitespace-pre-wrap break-words border border-ink-700">
          {rescue.response_raw}
        </pre>
      )}
    </article>
  );
}

function JsonBlock({
  title,
  value,
  collapsedByDefault,
}: {
  title: string;
  value: unknown;
  collapsedByDefault?: boolean;
}): JSX.Element {
  const [open, setOpen] = useState(!collapsedByDefault);
  const txt = useMemo(() => {
    try { return JSON.stringify(value, null, 2); }
    catch { return String(value); }
  }, [value]);

  return (
    <section className="rounded-lg border border-ink-700 bg-ink-800/40 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 hover:bg-ink-700/30 transition-colors"
      >
        <span className="text-xs text-ink-300 uppercase tracking-wider font-semibold">{title}</span>
        <ChevronRight
          className={`size-4 text-ink-500 transition-transform ${open ? "rotate-90" : ""}`}
        />
      </button>
      {open && (
        <pre className="px-3 pb-3 text-[11px] font-mono text-ink-300 overflow-x-auto whitespace-pre">
          {txt}
        </pre>
      )}
    </section>
  );
}

// Hint for `DSLStep` import only - the linter sees no usage otherwise
// because the type is only referenced in jsdoc. Kept as a no-op to avoid
// "imported but never used" warning under strict configs.
export type _DSLStep = DSLStep;
